import get from 'lodash/get';
import merge from 'lodash/merge';
import Logger from 'observability/Logger';
import catchInvalidatedTokenError from './catchInvalidatedTokenError';
import SplashApiClient, {
    HTTP_TOO_MANY_REQUESTS,
    RETRY_DELAY_MULTIPLIER_MILLISECONDS,
    TOO_MANY_REQUESTS_MAX_RETRIES,
    UNAUTHORIZED,
} from './SplashApiClient';

const LOG = new Logger('SplashAPIClientV2');
export const CLIENT_VERSION = '1.0.0';

const isNetworkError = (err) => err?.message?.match(/network/i);
const shouldRetryRateLimit = (errorStatus, retryAttempt) =>
    errorStatus === HTTP_TOO_MANY_REQUESTS && retryAttempt < TOO_MANY_REQUESTS_MAX_RETRIES;
const shouldRefreshToken = (errorStatus) => errorStatus === UNAUTHORIZED;

/**
 * SplashApiClientV2
 * =================
 *
 * Extends SplashApiClient with RESTful response handling.
 * Errors are indicated via HTTP status codes (>= 400) rather than
 * response body fields (meta.code, exp, error). Also refactors
 * the fetchWithRetry method with async/await.
 */
export default class SplashApiClientV2 extends SplashApiClient {
    static get VERSION() {
        return CLIENT_VERSION;
    }

    async fetchWithRetry(
        url,
        options,
        retry = this.retry,
        retryAttempt = 0,
        retryDelayMultiplier = RETRY_DELAY_MULTIPLIER_MILLISECONDS,
    ) {
        const { omitAccessToken, ...restOptions } = options;

        let opts = merge(this.defaultOptions(), restOptions);
        if (!omitAccessToken) {
            opts.headers = this.addAccessTokenHeader(opts.headers);
        }

        try {
            const response = await this.fetch({ url, ...opts });
            LOG.error({ splash_request_details: { url, method: options?.method, response: response.data } });
            return response.data;
        } catch (err) {
            if (!retry) throw err?.response ?? err;

            // Dev-only network error hint
            if (__DEVELOPMENT__ && isNetworkError(err)) {
                // eslint-disable-next-line no-console
                console.error(
                    `Hi there! If you're seeing this message, it probably means that you need to accept the self-signed certificate from ${this.baseUrl} to make the local Splash API work in your browser!`,
                );
                throw err;
            }

            // TODO: The current shouldRefreshToken logic needs to be updated with the backend changes
            // and be made more robust. Currently we will retry after refreshing token on any 401 status.
            const errorStatus = get(err, 'response.status');

            // Retry on rate limit
            if (shouldRetryRateLimit(errorStatus, retryAttempt)) {
                await new Promise((resolve) => setTimeout(resolve, retryAttempt * retryDelayMultiplier));
                return this.fetchWithRetry(url, opts, retry, retryAttempt + 1, retryDelayMultiplier);
            }

            // Refresh token and retry once
            if (shouldRefreshToken(errorStatus)) {
                try {
                    await this.refreshAccessToken();
                    opts = merge(this.defaultOptions(), options);
                    opts.headers = this.addAccessTokenHeader(opts.headers);
                    const retryResponse = await this.fetch({ url, ...opts });
                    return retryResponse.data;
                } catch {
                    throw err.response;
                }
            }

            catchInvalidatedTokenError(err.response);
            throw err.response;
        }
    }
}
